import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Storefront,
  Palette,
  Printer,
  Cube,
  Briefcase,
  SealCheck,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { products } from "@/data/mockData";

const filters = [
  { key: "all", label: "TODO", icon: Storefront },
  { key: "original", label: "ORIGINALES", icon: Palette },
  { key: "print", label: "PRINTS & DIGITAL", icon: Printer },
  { key: "nft", label: "NFT", icon: Cube },
  { key: "commission", label: "COMISIONES", icon: Briefcase },
];

const typeColor = {
  original: "#FF3E00",
  print: "#00FF66",
  nft: "#0033FF",
  commission: "#FF0033",
};

const typeLabel = {
  original: "ORIGINAL",
  print: "PRINT",
  nft: "NFT",
  commission: "COMISIÓN",
};

function fmt(price, currency) {
  if (currency === "ETH") return `Ξ ${price}`;
  return `${currency === "USD" ? "$" : "€"}${price.toLocaleString("es")}`;
}

export default function Marketplace() {
  const [filter, setFilter] = useState("all");
  const list = useMemo(
    () => (filter === "all" ? products : products.filter((p) => p.type === filter)),
    [filter]
  );
  const totalSold = products.reduce((acc, p) => acc + (p.sold || 0), 0);

  return (
    <Layout wide>
      <div data-testid="marketplace-page">
        <header className="p-4 md:p-8 border-b border-[#27272A]">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
            MARKETPLACE · {products.length} PIEZAS · {totalSold} VENDIDAS ESTE MES
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter uppercase leading-none">
            Compra
            <br />
            <span className="text-[#FF3E00]">arte vivo</span>.
          </h1>
          <p className="font-mono text-sm text-[#A1A1AA] mt-5 max-w-2xl leading-relaxed">
            // EL 92% DEL PRECIO VA DIRECTO AL ARTISTA. SIN INTERMEDIARIOS,
            SIN GALERISTAS. PAGA EN EUR · USD · ETH.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            {filters.map((f) => {
              const Icon = f.icon;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  data-testid={`filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                    active
                      ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                      : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5]"
                  }`}
                >
                  <Icon size={13} weight={active ? "fill" : "regular"} />
                  {f.label}
                </button>
              );
            })}
          </div>
        </header>

        <section className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {list.map((p) => (
            <Link
              key={p.id}
              to={`/tienda/${p.id}`}
              data-testid={`product-${p.id}`}
              className="group block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a] transition-colors"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                  <span
                    className={`font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 ${
                      typeColor[p.type] === "#FF3E00" || typeColor[p.type] === "#FF0033" || typeColor[p.type] === "#0033FF"
                        ? "text-white"
                        : "text-black"
                    }`}
                    style={{ background: typeColor[p.type] }}
                  >
                    {typeLabel[p.type]}
                  </span>
                  {p.sold ? (
                    <span className="font-mono text-[9px] text-white bg-black/70 backdrop-blur-sm border border-white/20 px-2 py-1 tracking-wider">
                      {p.sold} VENDIDOS
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="p-4 border-t border-[#27272A]">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold tracking-tight leading-tight line-clamp-2 flex-1">
                    {p.title}
                  </h3>
                  <div className="font-display font-black text-[#FF3E00] text-lg whitespace-nowrap">
                    {fmt(p.price, p.currency)}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#27272A]">
                  <img
                    src={p.artist.avatar}
                    alt={p.artist.name}
                    className="h-6 w-6 object-cover border border-[#27272A]"
                  />
                  <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate flex items-center gap-1">
                    @{p.artist.handle}
                    {p.artist.verified ? (
                      <SealCheck size={10} weight="fill" className="text-[#FF3E00]" />
                    ) : null}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </section>
      </div>
    </Layout>
  );
}
